/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * 数据库方言适配器
 *
 * 提供数据库特定的 SQL 生成功能，支持 Superset 支持的所有数据库类型。
 * 基于后端 db_engine_specs 模块的实现，为前端提供一致的接口。
 */

/**
 * 数据库后端类型定义
 * 与后端 superset.db_engine_specs 中的 engine 名称对应
 */
export type DatabaseBackend =
  | 'mysql'
  | 'postgresql'
  | 'postgres'
  | 'mariadb'
  | 'mssql'
  | 'oracle'
  | 'sqlite'
  | 'bigquery'
  | 'snowflake'
  | 'redshift'
  | 'presto'
  | 'hive'
  | 'trino'
  | 'clickhouse'
  | 'databricks'
  | 'athena'
  | 'doris'
  | 'starrocks'
  | 'druid'
  | 'elasticsearch'
  | 'pinot'
  | 'db2'
  | 'hana'
  | 'impala'
  | 'kylin'
  | 'netezza'
  | 'teradata'
  | 'vertica'
  | 'firebird'
  | 'duckdb'
  | string;

/**
 * 标识符引用配置
 */
export interface IdentifierQuote {
  /** 开始引用符号 */
  start: string;
  /** 结束引用符号 */
  end: string;
}

/**
 * 数据库方言配置
 */
export interface DatabaseDialect {
  /** 标识符引用符号 */
  identifierQuote: IdentifierQuote;
  /** 是否支持 FULL OUTER JOIN */
  supportsFullOuterJoin: boolean;
  /** 是否支持 CROSS JOIN */
  supportsCrossJoin: boolean;
  /** 是否支持 RIGHT JOIN */
  supportsRightJoin: boolean;
  /** 支持的 JOIN 类型 */
  supportedJoinTypes: string[];
  /** 日期格式化函数 */
  dateFormatFunction?: string;
  /** 是否区分大小写 */
  caseSensitive: boolean;
  /** 字符串连接操作符 */
  stringConcatOperator: string;
}

/**
 * 默认方言配置（标准 SQL）
 */
const DEFAULT_DIALECT: DatabaseDialect = {
  identifierQuote: { start: '"', end: '"' },
  supportsFullOuterJoin: false,
  supportsCrossJoin: true,
  supportsRightJoin: true,
  supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
  caseSensitive: true,
  stringConcatOperator: '||',
};

/**
 * 各数据库方言配置映射
 * 基于 Superset 后端 db_engine_specs 实现
 */
const DIALECTS: Record<string, DatabaseDialect> = {
  // MySQL 系
  mysql: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },
  mariadb: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },

  // PostgreSQL 系
  postgresql: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
  postgres: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
  redshift: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
  cockroachdb: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // SQL Server
  mssql: {
    identifierQuote: { start: '[', end: ']' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: '+',
  },

  // Oracle
  oracle: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // SQLite
  sqlite: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: false,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT'],
    caseSensitive: false,
    stringConcatOperator: '||',
  },

  // BigQuery
  bigquery: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },

  // Snowflake
  snowflake: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Presto/Trino 系
  presto: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
  trino: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
  athena: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Hive 系
  hive: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },
  impala: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },
  databricks: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },

  // ClickHouse
  clickhouse: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: 'concat',
  },

  // Doris/StarRocks
  doris: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },
  starrocks: {
    identifierQuote: { start: '`', end: '`' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: false,
    stringConcatOperator: 'CONCAT',
  },

  // Druid
  druid: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: false,
    supportedJoinTypes: ['INNER', 'LEFT', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Elasticsearch
  elasticsearch: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: false,
    supportsRightJoin: false,
    supportedJoinTypes: ['INNER', 'LEFT'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Pinot
  pinot: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: false,
    supportsRightJoin: false,
    supportedJoinTypes: ['INNER', 'LEFT'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // DB2
  db2: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // SAP HANA
  hana: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Netezza
  netezza: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Kylin
  kylin: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: false,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // DuckDB
  duckdb: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },

  // Firebird
  firebird: {
    identifierQuote: { start: '"', end: '"' },
    supportsFullOuterJoin: true,
    supportsCrossJoin: true,
    supportsRightJoin: true,
    supportedJoinTypes: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER', 'CROSS'],
    caseSensitive: true,
    stringConcatOperator: '||',
  },
};

/**
 * 获取数据库方言配置
 * @param backend 数据库后端类型
 * @returns 数据库方言配置
 */
export function getDialect(backend?: DatabaseBackend): DatabaseDialect {
  const normalizedBackend = backend?.toLowerCase().trim();
  return DIALECTS[normalizedBackend || ''] || DEFAULT_DIALECT;
}

/**
 * 引用标识符（表名、列名、别名等）
 * @param identifier 标识符
 * @param backend 数据库后端类型
 * @returns 引用后的标识符
 *
 * @example
 * quoteIdentifier('table_name', 'mysql')     // `table_name`
 * quoteIdentifier('table_name', 'postgresql') // "table_name"
 * quoteIdentifier('column', 'mssql')          // [column]
 */
export function quoteIdentifier(
  identifier: string,
  backend?: DatabaseBackend,
): string {
  if (!identifier) return identifier;

  const dialect = getDialect(backend);
  const { start, end } = dialect.identifierQuote;

  // 如果已经包含引用符号，先移除
  const cleanIdentifier = identifier
    .replace(new RegExp(`^${escapeRegex(start)}`), '')
    .replace(new RegExp(`${escapeRegex(end)}$`), '');

  return `${start}${cleanIdentifier}${end}`;
}

/**
 * 引用模式限定的标识符（schema.table）
 * @param schema 模式名
 * @param identifier 标识符
 * @param backend 数据库后端类型
 * @returns 引用后的完整标识符
 *
 * @example
 * quoteQualifiedIdentifier('public', 'users', 'postgresql') // "public"."users"
 */
export function quoteQualifiedIdentifier(
  schema: string | undefined,
  identifier: string,
  backend?: DatabaseBackend,
): string {
  if (!identifier) return identifier;

  const quotedIdentifier = quoteIdentifier(identifier, backend);
  if (schema) {
    const quotedSchema = quoteIdentifier(schema, backend);
    return `${quotedSchema}.${quotedIdentifier}`;
  }
  return quotedIdentifier;
}

/**
 * 转义 SQL 字符串值
 * 防止 SQL 注入
 * @param value 原始字符串值
 * @param backend 数据库后端类型
 * @returns 转义后的值（包含引号）
 *
 * @example
 * escapeSqlValue("O'Brien") // 'O''Brien'
 * escapeSqlValue("test")    // 'test'
 */
export function escapeSqlValue(
  value: string,
  backend?: DatabaseBackend,
): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  // 转义单引号：单引号前再加一个单引号
  const escaped = String(value).replace(/'/g, "''");
  return `'${escaped}'`;
}

/**
 * 构建安全的 SQL 值
 * @param value 任意类型的值
 * @param backend 数据库后端类型
 * @returns 安全的 SQL 值字符串
 */
export function buildSafeSqlValue(
  value: unknown,
  backend?: DatabaseBackend,
): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }

  if (Array.isArray(value)) {
    const values = value.map(v => buildSafeSqlValue(v, backend));
    return `(${values.join(', ')})`;
  }

  return escapeSqlValue(String(value), backend);
}

/**
 * 构建 AS 别名表达式
 * @param expression SQL 表达式
 * @param alias 别名
 * @param backend 数据库后端类型
 * @returns 带 AS 的完整表达式
 *
 * @example
 * buildAliasedExpression('COUNT(*)', 'total', 'mysql') // COUNT(*) AS `total`
 */
export function buildAliasedExpression(
  expression: string,
  alias: string,
  backend?: DatabaseBackend,
): string {
  const quotedAlias = quoteIdentifier(alias, backend);
  return `${expression} AS ${quotedAlias}`;
}

/**
 * 获取支持的 JOIN 类型列表
 * @param backend 数据库后端类型
 * @returns 支持的 JOIN 类型数组
 */
export function getSupportedJoinTypes(backend: DatabaseBackend): string[] {
  const dialect = getDialect(backend);
  return [...dialect.supportedJoinTypes];
}

/**
 * 检查是否支持指定的 JOIN 类型
 * @param joinType JOIN 类型
 * @param backend 数据库后端类型
 * @returns 是否支持
 */
export function isJoinTypeSupported(
  joinType: string,
  backend: DatabaseBackend,
): boolean {
  const supportedTypes = getSupportedJoinTypes(backend);
  return supportedTypes.includes(joinType.toUpperCase());
}

/**
 * 获取 JOIN 类型的 UI 标签配置
 * @param backend 数据库后端类型
 * @returns JOIN 类型选项数组
 */
export function getJoinTypeOptions(
  backend: DatabaseBackend,
): Array<{ value: string; label: string; enLabel: string }> {
  const supportedTypes = getSupportedJoinTypes(backend);

  const joinTypeLabels: Record<string, { label: string; enLabel: string }> = {
    INNER: { label: '内连接 (INNER JOIN)', enLabel: 'INNER JOIN' },
    LEFT: { label: '左连接 (LEFT JOIN)', enLabel: 'LEFT JOIN' },
    RIGHT: { label: '右连接 (RIGHT JOIN)', enLabel: 'RIGHT JOIN' },
    'FULL OUTER': {
      label: '全外连接 (FULL OUTER JOIN)',
      enLabel: 'FULL OUTER JOIN',
    },
    CROSS: { label: '交叉连接 (CROSS JOIN)', enLabel: 'CROSS JOIN' },
  };

  return supportedTypes.map(type => ({
    value: type,
    ...joinTypeLabels[type],
  }));
}

/**
 * 转义正则表达式特殊字符
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 构建表引用（带别名）
 * @param schema 模式名
 * @param tableName 表名
 * @param alias 别名
 * @param backend 数据库后端类型
 * @returns 完整的表引用
 *
 * @example
 * buildTableReference('public', 'users', 'u', 'postgresql')
 * // "public"."users" u
 */
export function buildTableReference(
  schema: string | undefined,
  tableName: string,
  alias: string,
  backend: DatabaseBackend,
): string {
  const qualifiedName = quoteQualifiedIdentifier(schema, tableName, backend);
  return `${qualifiedName} ${alias}`;
}

/**
 * 构建列引用（带表别名）
 * @param tableAlias 表别名
 * @param columnName 列名
 * @param backend 数据库后端类型
 * @returns 完整的列引用
 *
 * @example
 * buildColumnReference('u', 'name', 'mysql') // u.`name`
 * buildColumnReference('u', 'name', 'postgresql') // u."name"
 */
export function buildColumnReference(
  tableAlias: string,
  columnName: string,
  backend: DatabaseBackend,
): string {
  const quotedColumn = quoteIdentifier(columnName, backend);
  return `${tableAlias}.${quotedColumn}`;
}

/**
 * 构建带表别名的列引用（简化版，不加引号）
 * 某些数据库不需要对列名加引号
 * @param tableAlias 表别名
 * @param columnName 列名
 * @returns 列引用
 */
export function buildSimpleColumnReference(
  tableAlias: string,
  columnName: string,
): string {
  return `${tableAlias}.${columnName}`;
}

/**
 * 获取数据库特性信息（用于 UI 提示）
 * @param backend 数据库后端类型
 * @returns 特性描述对象
 */
export function getDatabaseFeatures(backend: DatabaseBackend) {
  const dialect = getDialect(backend);
  return {
    supportsFullOuterJoin: dialect.supportsFullOuterJoin,
    supportsCrossJoin: dialect.supportsCrossJoin,
    supportedJoinTypes: dialect.supportedJoinTypes,
    identifierQuote: dialect.identifierQuote,
    caseSensitive: dialect.caseSensitive,
  };
}

export default {
  getDialect,
  quoteIdentifier,
  quoteQualifiedIdentifier,
  escapeSqlValue,
  buildSafeSqlValue,
  buildAliasedExpression,
  getSupportedJoinTypes,
  isJoinTypeSupported,
  getJoinTypeOptions,
  buildTableReference,
  buildColumnReference,
  buildSimpleColumnReference,
  getDatabaseFeatures,
};
